import { Component, inject, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from 'src/app/data.service';

interface FbfEntry {
	FBF: string;
	FBF_display: string;
	name: string;
	name_lang?: string;
	HBF: string;
	NKr: string;
	Serie: string | null;
}

type SortColumn = 'FBF_display' | 'name_lang' | 'HBF' | 'NKr' | 'Serie';
type SortDirection = 'asc' | 'desc';

@Component({
    standalone: false,
	selector: 'bp-fbf-picker-modal',
	template: `
		<div class="modal-header">
			<h5 class="modal-title">Feinbodenform auswählen</h5>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			<div class="mb-3">
				<input
					type="text"
					class="form-control"
					placeholder="Suchen nach FBF, Name, HBF, Serie..."
					[(ngModel)]="searchTerm"
					(ngModelChange)="onSearch()"
					#searchInput
				/>
			</div>
			<div class="table-container">
				<table class="table table-sm table-hover">
					<thead class="sticky-top bg-white">
						<tr>
							<th class="sortable" (click)="sort('FBF_display')">
								FBF
								<span class="sort-icon">{{getSortIcon('FBF_display')}}</span>
							</th>
							<th class="sortable" (click)="sort('name_lang')">
								Name
								<span class="sort-icon">{{getSortIcon('name_lang')}}</span>
							</th>
							<th class="sortable" (click)="sort('HBF')">
								HBF
								<span class="sort-icon">{{getSortIcon('HBF')}}</span>
							</th>
							<th class="sortable" (click)="sort('NKr')">
								NKr
								<span class="sort-icon">{{getSortIcon('NKr')}}</span>
							</th>
							<th class="sortable" (click)="sort('Serie')">
								Serie
								<span class="sort-icon">{{getSortIcon('Serie')}}</span>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr
							*ngFor="let item of filteredData"
							(click)="select(item)"
							class="cursor-pointer"
							[class.table-primary]="item === selectedItem"
						>
							<td><strong>{{item.FBF_display}}</strong></td>
							<td>{{item.name_lang || item.name}}</td>
							<td>{{item.HBF}}</td>
							<td>{{item.NKr}}</td>
							<td>{{item.Serie}}</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div class="mt-2 text-muted small">
				{{filteredData.length}} von {{allData.length}} Einträgen
			</div>
		</div>
		<div class="modal-footer">
			<button type="button" class="btn btn-secondary" (click)="activeModal.dismiss()">Abbrechen</button>
			<button type="button" class="btn btn-primary" [disabled]="!selectedItem" (click)="confirm()">Übernehmen</button>
		</div>
	`,
	styles: [`
		.table-container {
			max-height: 400px;
			overflow-y: auto;
		}
		.cursor-pointer {
			cursor: pointer;
		}
		.cursor-pointer:hover {
			background-color: #f0f0f0;
		}
		th {
			position: sticky;
			top: 0;
			background: white;
			z-index: 1;
		}
		th.sortable {
			cursor: pointer;
			user-select: none;
		}
		th.sortable:hover {
			background-color: #f0f0f0;
		}
		.sort-icon {
			margin-left: 4px;
			opacity: 0.5;
		}
	`],
	
})
export class FbfPickerModalComponent implements OnInit {
	@Input() currentValue?: string;
	@Input() hbfFilter?: string;

	private ds = inject(DataService);
	public activeModal = inject(NgbActiveModal);

	allData: FbfEntry[] = [];
	filteredData: FbfEntry[] = [];
	searchTerm = '';
	selectedItem?: FbfEntry;
	sortColumn: SortColumn = 'FBF_display';
	sortDirection: SortDirection = 'asc';

	ngOnInit() {
		this.allData = this.ds.getHardcoded('Feinbodenformen') as FbfEntry[];
		this.filteredData = [...this.allData];
		this.applySort();

		// Wenn aktueller Wert gesetzt, als Suchterm verwenden
		if (this.currentValue) {
			this.searchTerm = this.currentValue;
			this.onSearch();
			// Versuche aktuellen Wert zu finden
			this.selectedItem = this.allData.find(item => item.FBF === this.currentValue);
		}
	}

	onSearch() {
		const term = this.searchTerm.trim().toLowerCase();
		if (!term) {
			this.filteredData = [...this.allData];
		} else {
			this.filteredData = this.allData.filter(item =>
				item.FBF_display?.toLowerCase().includes(term) ||
				item.FBF?.toLowerCase().includes(term) ||
				item.name?.toLowerCase().includes(term) ||
				item.name_lang?.toLowerCase().includes(term) ||
				item.HBF?.toLowerCase().includes(term) ||
				item.NKr?.toLowerCase().includes(term) ||
				item.Serie?.toLowerCase().includes(term)
			);
		}
		this.applySort();
	}

	sort(column: SortColumn) {
		if (this.sortColumn === column) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = column;
			this.sortDirection = 'asc';
		}
		this.applySort();
	}

	applySort() {
		this.filteredData.sort((a, b) => {
			let valA = a[this.sortColumn] || '';
			let valB = b[this.sortColumn] || '';

			// Für name_lang: Fallback auf name wenn leer
			if (this.sortColumn === 'name_lang') {
				valA = a.name_lang || a.name || '';
				valB = b.name_lang || b.name || '';
			}

			const comparison = valA.localeCompare(valB, 'de');
			return this.sortDirection === 'asc' ? comparison : -comparison;
		});
	}

	getSortIcon(column: SortColumn): string {
		if (this.sortColumn !== column) return '↕';
		return this.sortDirection === 'asc' ? '↑' : '↓';
	}

	select(item: FbfEntry) {
		this.selectedItem = item;
	}

	confirm() {
		if (this.selectedItem) {
			// Gib FBF (ohne Leerzeichen) und HBF zurück
			this.activeModal.close({
				fbf: this.selectedItem.FBF,
				hbf: this.selectedItem.HBF
			});
		}
	}
}
